/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */

import React, { useState } from 'react';

import { useIntl } from 'react-intl';
import { useFragment } from 'react-relay';
import { useRouterActions } from 'react-resource-router';
import { graphql } from 'relay-runtime';

import Button from '@atlaskit/button/button';
import { cssMap, jsx } from '@atlaskit/css';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import ButtonItem from '@atlaskit/menu/button-item';
import Section from '@atlaskit/menu/section';
import ExitingPersistence from '@atlaskit/motion/exiting-persistence';
import Motion from '@atlaskit/motion/entering/motion';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import { HelpPageAri } from '@atlassian/ari/help';

import type { topicDropdown_AtlassianHubAppShell$key } from './__generated__/topicDropdown_AtlassianHubAppShell.graphql';
import messages from './messages';

const styles = cssMap({
	overflowHidden: {
		overflow: 'hidden',
	},
	dropdownItems: {
		paddingLeft: token('space.100'),
	},
});

const motionStyles = cssMap({
	slideEntering: {
		animationName: `${token('motion.keyframe.slide.in.top.short')}, ${token('motion.keyframe.fade.in')}`,
		animationDuration: token('motion.duration.medium'),
		animationTimingFunction: token('motion.easing.out.practical'),
	},
	slideExiting: {
		animationName: `${token('motion.keyframe.slide.out.top.short')}, ${token('motion.keyframe.fade.out')}`,
		animationDuration: token('motion.duration.medium'),
		animationTimingFunction: token('motion.easing.in.practical'),
	},
});

const getPageIdFromAri = (pageAri: string): string | null => {
	try {
		return HelpPageAri.parse(pageAri).pageId;
	} catch {
		return null;
	}
};

/**
 * The navbar's Topics menu.
 *
 * Unlike `PortalDropdown`, a single topic still renders as the `Topics` menu
 * rather than collapsing to the topic's own name: "Topics" is a category, so
 * collapsing it would put one topic's title in the navbar and reshape the nav as
 * topics come and go.
 *
 * Draft handling needs nothing here — `helpCenterById` is already resolved with
 * `isDraft`, so the parent query decides whether draft or live topics come back,
 * exactly as it does for the custom pages in `MoreDropdown`.
 */
export const TopicDropdown = ({
	queryRef,
	textColor,
	isMobile,
}: {
	isMobile?: boolean;
	queryRef: topicDropdown_AtlassianHubAppShell$key | null;
	textColor?: string;
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { push } = useRouterActions();
	const [isExpanded, setIsExpanded] = useState(false);

	const data = useFragment(
		graphql`
			fragment topicDropdown_AtlassianHubAppShell on HelpCenter {
				topicPages: pages(filter: { ids: [], type: TOPIC }) {
					id
					name {
						default
					}
				}
			}
		`,
		queryRef,
	);

	const topicPages = data?.topicPages;

	// Nothing to open onto, so the menu stays out of the navbar entirely.
	if (!fg('hubs_premium_upsell') || !topicPages || topicPages.length === 0) {
		return null;
	}

	const openTopic = (pageAri: string | undefined) => {
		const pageId = pageAri ? getPageIdFromAri(pageAri) : null;
		if (pageId) {
			push(`/page/${pageId}`);
		}
	};

	const topicTestId = (pageAri: string | undefined) =>
		`nav-topic-page-${(pageAri ? getPageIdFromAri(pageAri) : null) ?? ''}`;

	if (isMobile) {
		return (
			<>
				<ButtonItem
					iconAfter={
						isExpanded ? (
							<ChevronUpIcon label={formatMessage(messages.topicUpIcon)} />
						) : (
							<ChevronDownIcon label={formatMessage(messages.topicDownIcon)} />
						)
					}
					onClick={() => setIsExpanded((prev) => !prev)}
					aria-expanded={isExpanded}
				>
					<Text weight="medium">{formatMessage(messages.topics)}</Text>
				</ButtonItem>
				<div css={styles.overflowHidden}>
					<ExitingPersistence appear>
						{isExpanded && (
							<Motion
								enteringAnimationXcss={motionStyles.slideEntering}
								exitingAnimationXcss={motionStyles.slideExiting}
							>
								<div css={styles.dropdownItems} data-testid="topic-dropdown-items">
									<Section>
										{topicPages.map((topic) => (
											<ButtonItem
												key={topic?.id}
												testId={topicTestId(topic?.id)}
												onClick={() => openTopic(topic?.id)}
											>
												{topic?.name?.default ?? ''}
											</ButtonItem>
										))}
									</Section>
								</div>
							</Motion>
						)}
					</ExitingPersistence>
				</div>
			</>
		);
	}

	return (
		<DropdownMenu<HTMLButtonElement>
			trigger={({ triggerRef, ...props }) => (
				<Button
					{...props}
					appearance="subtle"
					style={{ color: props.isSelected ? undefined : textColor }}
					iconAfter={<ChevronDownIcon label={formatMessage(messages.topicDownIcon)} />}
					ref={triggerRef}
					testId="topic-dropdown-trigger"
				>
					{formatMessage(messages.topics)}
				</Button>
			)}
			shouldRenderToParent
		>
			<DropdownItemGroup>
				{topicPages.map((topic) => (
					<DropdownItem
						key={topic?.id}
						testId={topicTestId(topic?.id)}
						onClick={() => openTopic(topic?.id)}
					>
						{topic?.name?.default ?? ''}
					</DropdownItem>
				))}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};
